import React, { useState } from 'react'
import Tippy from '@tippyjs/react'
import 'tippy.js/dist/tippy.css'
import { Sentiment } from '../types'
import { getSentimentColor } from './SentimentIndicator'

interface SentimentOption {
	sentiment: Sentiment
	color: string
	label: string
}

const sentimentOptionsStatic: SentimentOption[] = [
	{ sentiment: 1, color: '#19DA8F', label: 'Positive' },
	{ sentiment: 0, color: '#A7A7A7', label: 'Neutral' },
	{ sentiment: -1, color: '#FF4955', label: 'Negative' },
]

interface SentimentTooltipProps {
	currentSentiment: Sentiment
	onSentimentChange: (newSentiment: Sentiment) => void
	children: React.ReactElement
	translations?: Record<string, string> | null
}

const SentimentTooltip: React.FC<SentimentTooltipProps> = ({
	currentSentiment,
	onSentimentChange,
	children,
	translations,
}) => {
	const [visible, setVisible] = useState(false)

	const sentimentOptions: SentimentOption[] = [
		{ sentiment: 1, color: '#19DA8F', label: translations?.positive || 'Positive' },
		{ sentiment: 0, color: '#A7A7A7', label: translations?.neutral || 'Neutral' },
		{ sentiment: -1, color: '#FF4955', label: translations?.negative || 'Negative' },
	]

	const renderSentimentOption = (option: SentimentOption) => {
		const isSelected = option.sentiment === currentSentiment

		return (
			<li
				key={option.sentiment ?? 'null'}
				className="cursor-pointer"
				onClick={() => {
					onSentimentChange(option.sentiment)
					setVisible(false)
				}}
				title={option.label}
			>
				{isSelected ? (
					<div
						style={{ borderColor: option.color }}
						className="h-[24px] w-[24px] flex border justify-center items-center rounded-md outline-offset-0"
					>
						{option.sentiment === null ? (
							<i className="fa-light fa-empty-set text-gray-500"></i>
						) : (
							<div style={{ background: option.color }} className="h-[16px] w-[16px] rounded-md"></div>
						)}
					</div>
				) : option.sentiment === null ? (
					<i className="fa-light fa-empty-set text-gray-500"></i>
				) : (
					<div style={{ background: option.color }} className="h-[16px] w-[16px] rounded-md"></div>
				)}
			</li>
		)
	}

	return (
		<Tippy
			interactive={true}
			visible={visible}
			onClickOutside={() => setVisible(false)}
			theme="tooltip-select"
			popperOptions={{ strategy: 'fixed' }}
			arrow={false}
			trigger="click"
			content={
				<ul className="flex space-x-2 justify-center items-center">
					{sentimentOptions.map((option) => renderSentimentOption(option))}
				</ul>
			}
		>
			{React.cloneElement(children, {
				onClick: (e: React.MouseEvent) => {
					e.stopPropagation()
					setVisible(!visible)
				},
			})}
		</Tippy>
	)
}

export default SentimentTooltip
